import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Check, ArrowRight, HelpCircle, ShieldCheck, Timer, Receipt, BadgeCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { getPrestation, getCategory, prestationList, REVIEWS, prestaImage, CATEGORY_FAQ } from "../lib/prestations";
import { COMPANY } from "../lib/data";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import BrandMarquee from "../components/BrandMarquee";
import GoogleReviews from "../components/GoogleReviews";
import GoogleMap from "../components/GoogleMap";
import InfoStrip from "../components/InfoStrip";
import QuoteForm from "../components/QuoteForm";

const ENGAGEMENTS = [
  { Icon: Timer, t: "Rapidité", d: "Sur place en 30 min en moyenne, 24h/24 et 7j/7." },
  { Icon: Receipt, t: "Devis clair", d: "Tarif annoncé avant l'intervention, sans surprise." },
  { Icon: ShieldCheck, t: "Artisans assurés", d: "Professionnels certifiés et couverts par assurance." },
  { Icon: BadgeCheck, t: "Travail garanti", d: "Pièces et main-d'œuvre garanties, facture détaillée." },
];

export default function PrestationPage() {
  const { category, slug } = useParams();
  const p = getPrestation(slug);
  const cat = getCategory(category);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!p || !cat || p.cat !== category || !cat.pages.includes(slug)) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center text-center px-5">
        <h1 className="font-display font-extrabold text-ink text-4xl">Page introuvable</h1>
        <Link to="/" className="btn-primary mt-8">Retour à l&apos;accueil</Link>
      </main>
    );
  }

  const siblings = prestationList(category).filter((s) => s.slug !== slug).slice(0, 6);
  const gallery = [prestaImage(slug), cat.image, ...siblings.slice(0, 2).map((s) => prestaImage(s.slug))].slice(0, 4);
  const faqAll = [...p.faq, ...(CATEGORY_FAQ[category] || [])];

  return (
    <main data-testid={`prestation-${slug}`} className="bg-white">
      <Seo path={`/${category}/${slug}`} title={p.title.replace(" | Le Bon Dépanneur", "")}
        description={p.metaDescription} image={p.image} />

      {/* Banner */}
      <section className="relative pt-28 md:pt-32 pb-14 md:pb-20 overflow-hidden bg-slate-50 border-b border-slate-200">
        <div className="pointer-events-none absolute -top-16 right-0 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <nav className="flex items-center gap-2 text-sm text-ink-muted mb-5" aria-label="Fil d'ariane">
              <Link to="/" className="hover:text-brand">Accueil</Link>
              <span>/</span>
              <Link to={`/${category}`} className="hover:text-brand">{cat.name}</Link>
              <span>/</span>
              <span className="text-ink font-medium">{p.h1}</span>
            </nav>
            <span className="chip">{cat.name} · Urgence 24/7</span>
            <h1 className="mt-4 font-display font-extrabold text-ink text-3xl md:text-5xl leading-[1.08]">
              {p.h1}
            </h1>
            <p className="mt-5 text-lg text-ink-soft leading-relaxed">{p.lead}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={COMPANY.phoneHref} data-testid="prestation-call-btn" className="btn-primary">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
              <a href="#devis" className="btn-outline">Devis gratuit</a>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[1.75rem] overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[16/10]"
          >
            <img src={prestaImage(slug)} alt={p.h1} fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      <BrandMarquee category={category} />

      {/* Content + sticky sidebar */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl">
                {p.h1} à {COMPANY.zone}
              </h2>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="mt-5 text-ink-soft text-lg leading-relaxed">{p.lead}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-ink-soft text-lg leading-relaxed">
                {COMPANY.name} met à votre disposition un réseau d&apos;artisans {cat.name.toLowerCase()} qualifiés,
                assurés et proches de chez vous. {cat.intro} Nous intervenons rapidement partout en Île-de-France,
                avec un devis clair communiqué avant chaque intervention.
              </p>
            </Reveal>

            {/* Points */}
            <Reveal delay={0.1}>
              <h3 className="mt-12 font-display font-bold text-ink text-2xl">Nos prestations</h3>
            </Reveal>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {p.points.map((pt, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="card-soft flex items-start gap-4 p-5 hover:translate-y-0">
                    <span className="h-8 w-8 rounded-lg bg-brand-green/10 flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4 text-brand-green" />
                    </span>
                    <span className="text-ink font-medium">{pt}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Process */}
            <Reveal delay={0.1}>
              <h3 className="mt-14 font-display font-bold text-ink text-2xl">Comment se déroule l&apos;intervention</h3>
            </Reveal>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {cat.process.map((st, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="card-soft p-6 h-full hover:translate-y-0">
                    <span className="font-display font-extrabold text-3xl gradient-text">{String(i + 1).padStart(2, "0")}</span>
                    <h4 className="font-display font-bold text-ink text-lg mt-3">{st.t}</h4>
                    <p className="text-ink-soft mt-2 text-sm leading-relaxed">{st.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Engagements */}
            <div className="mt-14 rounded-2xl bg-slate-50 border border-slate-200 p-6 md:p-8">
              <h3 className="font-display font-bold text-ink text-2xl mb-6">Nos engagements</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {ENGAGEMENTS.map((e, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 h-full">
                      <span className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center">
                        <e.Icon className="h-5 w-5 text-brand" />
                      </span>
                      <h4 className="font-display font-bold text-ink mt-4">{e.t}</h4>
                      <p className="text-ink-soft text-sm mt-1 leading-relaxed">{e.d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Gallery */}
            <Reveal delay={0.1}>
              <h3 className="mt-14 font-display font-bold text-ink text-2xl">En images</h3>
            </Reveal>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {gallery.map((src, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="rounded-xl overflow-hidden border border-slate-200 aspect-square">
                    <img src={src} alt={`${p.h1} ${i + 1}`} loading="lazy" decoding="async"
                      className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                </Reveal>
              ))}
            </div>

            {/* FAQ */}
            <Reveal delay={0.1}>
              <h3 className="mt-14 font-display font-bold text-ink text-2xl flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-brand" /> Questions fréquentes
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="w-full mt-6 space-y-3">
                {faqAll.map((item, i) => (
                  <AccordionItem key={i} value={`f-${i}`} data-testid={`faq-item-${i}`}
                    className="card-soft border border-slate-200 px-6 hover:translate-y-0">
                    <AccordionTrigger className="font-display font-bold text-ink text-base md:text-lg text-left hover:no-underline py-5 [&>svg]:text-brand">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-ink-soft text-base leading-relaxed pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="card-soft p-7 hover:translate-y-0 text-center">
                <p className="overline">Appelez immédiatement</p>
                <a href={COMPANY.phoneHref} className="block font-display font-extrabold text-ink text-3xl mt-2 hover:text-brand transition-colors">
                  {COMPANY.phone}
                </a>
                <p className="text-ink-soft text-sm mt-2">Un conseiller vous répond en moins de 2 minutes, 24h/24.</p>
                <a href={COMPANY.phoneHref} className="btn-primary w-full mt-5">
                  <Phone className="h-4 w-4" /> Appeler maintenant
                </a>
              </div>
              <div id="devis" className="card-soft p-6 md:p-7 hover:translate-y-0 scroll-mt-24">
                <h3 className="font-display font-bold text-ink text-xl mb-4">Demander un devis gratuit</h3>
                <QuoteForm defaultService={cat.slug === "electricite" ? "electricite" : cat.slug} source={slug} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Reviews (activity) */}
      <div className="bg-slate-50 border-y border-slate-200">
        <GoogleReviews reviews={REVIEWS[category]} title={`Avis clients — ${cat.name}`}
          subtitle="Des interventions notées par nos clients de toute l'Île-de-France." />
      </div>

      {/* Map + info */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-10">
            <GoogleMap className="min-h-[320px]" />
            <div className="flex flex-col justify-center">
              <span className="overline">Toujours près de vous</span>
              <h2 className="mt-3 font-display font-extrabold text-ink text-2xl md:text-4xl">
                Une intervention rapide dans toute l&apos;Île-de-France
              </h2>
              <p className="mt-4 text-ink-soft leading-relaxed">
                Nos artisans {cat.name.toLowerCase()} sillonnent Paris et sa région pour vous garantir un délai
                d&apos;intervention moyen de 30 minutes, de jour comme de nuit.
              </p>
            </div>
          </div>
          <InfoStrip />
        </div>
      </section>

      {/* Internal links */}
      <section className="bg-slate-50 border-t border-slate-200 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between mb-6">
            <span className="overline">Autres prestations {cat.name.toLowerCase()}</span>
            <Link to={`/${category}`} className="btn-link text-brand">
              Tout voir <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {siblings.map((s) => (
              <Link key={s.slug} to={`/${category}/${s.slug}`} data-testid={`related-${s.slug}`}
                className="card-soft group flex items-center justify-between p-5">
                <span className="font-semibold text-ink group-hover:text-brand transition-colors">{s.h1}</span>
                <ArrowRight className="h-5 w-5 text-ink-muted group-hover:text-brand transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
